// @flow
import React, { Fragment, PureComponent, type Element } from 'react';
import { connect } from 'react-redux';
import 'app/common/react/components/growth/activation/NuxGenderStep/NuxGenderStep.scss';
import i18n from 'app/common/lib/i18n';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { track } from 'app/common/lib/react/analytics';
import setNuxProgress, { type NuxStep } from 'app/common/redux/actions/nuxProgress';
import {
  COMPLETED,
  FEMALE,
  INLINE_GENDER_EDIT_STYLE,
  INLINE_GENDER_EDIT_STYLE_WITH_BUTTON,
  MALE,
  NUX_GENDER_STEP_STATUS,
  SHOWED,
  UNSPECIFIED,
} from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Button, Divider, Heading, Label, RadioButton, Text } from 'gestalt';
import { compose, type Dispatch } from 'redux';
import { updateUser, setUserState } from 'app/common/redux/actions/nux/updateUser';

type RadioButtonProps = {|
  isChecked: boolean,
  label: string,
  selectGender: () => void,
  value: Element<any>,
|};

const FullRadioButton = ({ isChecked, label, selectGender, value }: RadioButtonProps) => (
  <Fragment>
    <Box data-test-id={`nux-gender-${label}-label`}>
      <Label htmlFor={label}>
        <div className={'NuxGenderSelector'}>
          <Box alignItems="center" display="flex" flex="none" paddingX={8} paddingY={6}>
            <RadioButton
              checked={isChecked}
              id={label}
              name="genderOptions"
              onChange={selectGender}
              size="md"
              value={label}
            />
            <Box display="flex" flex="grow" marginStart={10}>
              {value}
            </Box>
          </Box>
        </div>
      </Label>
    </Box>
    <Divider />
  </Fragment>
);

type OwnProps = {|
  incrementNuxStep: () => void,
  isRenux?: boolean,
  personalizationCopy: {
    headerText: string,
    subHeaderText: string,
  },
  showTableOfContents?: boolean,
|};

type StateProps = {|
  gender: string,
  customGender: string,
|};

type DispatchProps = {|
  dispatchSetNuxStepProgress: (NuxStep) => void,
  setNuxGenderProgress: (number) => void,
  updateGenderInfo: ({
    gender?: string,
    custom_gender?: string,
  }) => Promise<any>,
|};

type HOCProps = {|
  requestContext: RequestContextType,
|};

type ConnectProps = {|
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
  ...HOCProps,
|};

type State = {|
  customGender: string,
  gender: string,
  showCustomGenderDoneButton: boolean,
  showCustomGenderTextfield: boolean,
  showNextButton: boolean, // Only appears when coming back to gender step 2nd time
|};

export class NuxGenderStep extends PureComponent<Props, State> {
  state: State = {
    customGender: this.props.customGender,
    gender: this.props.gender,
    // For the initial state, gender is unspecified. We should only show this textfield
    // in the initial state if it is already filled in.
    showCustomGenderTextfield:
      this.props.gender === UNSPECIFIED && this.props.customGender.length > 0,
    showNextButton:
      [MALE, FEMALE].includes(this.props.gender) || this.props.customGender.length > 0,
    // Note: if showNextButton is true, this button no longer shows up
    showCustomGenderDoneButton:
      this.props.gender === UNSPECIFIED && this.props.customGender.length > 0,
  };

  componentDidMount() {
    const { dispatchSetNuxStepProgress, isRenux, setNuxGenderProgress } = this.props;

    dispatchSetNuxStepProgress('gender');

    if (!isRenux) {
      setNuxGenderProgress(SHOWED);
    }

    track(PCONST.thrift.EventType.VIEW, {
      view: isRenux ? PCONST.thrift.ViewType.REDO_ORIENTATION : PCONST.thrift.ViewType.ORIENTATION,
      viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_GENDER_STEP,
    });
  }

  onSelectGender(selectedGender: 'unspecified' | 'female' | 'male') {
    const { requestContext } = this.props;
    const { showCustomGenderTextfield, showNextButton } = this.state;

    if (showNextButton) {
      this.setState({
        gender: selectedGender,
        showCustomGenderTextfield: selectedGender === UNSPECIFIED,
      });
    } else if (
      selectedGender === UNSPECIFIED &&
      new SingleTreatmentExp(requestContext, 'web_hide_custom_gender_field').enabled()
    ) {
      this.setState(
        { gender: UNSPECIFIED, showCustomGenderTextfield: false },
        this.updateGenderCompleteStep,
      );
    } else if (selectedGender === UNSPECIFIED || showCustomGenderTextfield) {
      // Toggle custom gender text field if necessary. Note, we do not complete step if user clicks on male/female while
      // custom field is open. Otherwise, set gender and complete step.
      this.setState({
        gender: UNSPECIFIED,
        customGender: '',
        showCustomGenderTextfield: !showCustomGenderTextfield,
        showCustomGenderDoneButton: false,
      });
    } else {
      this.setState({ gender: selectedGender }, this.updateGenderCompleteStep);
    }
  }

  setCustomGender = (event: SyntheticEvent<>) => {
    // Set gender state to CUSTOM and actually user specified custom gender separately
    if (event.target instanceof HTMLInputElement && event.target.value.trim().length > 0) {
      this.setState({
        customGender: event.target.value,
        showCustomGenderDoneButton: true,
      });
    } else if (this.state.showNextButton && event.target instanceof HTMLInputElement) {
      // If showing next button, we want to update customGender even when length reaches 0.
      // This is because the step doesn't auto-progress by clicking on a radio button
      this.setState({
        customGender: event.target.value,
        showCustomGenderDoneButton: false,
      });
    } else {
      this.setState({
        showCustomGenderDoneButton: false,
      });
    }
  };

  updateCustomGender = ({ value: customGender }: Object) =>
    this.setState({ customGender, showCustomGenderDoneButton: customGender.length > 0 });

  setUserStateCompletedGenderStep() {
    const { isRenux, setNuxGenderProgress } = this.props;
    if (!isRenux) {
      setNuxGenderProgress(COMPLETED);
    }
    // Resource call is for mweb compatibility. Remove if we deprecate this UserState
  }

  updateGenderCompleteStep() {
    const { incrementNuxStep, isRenux, updateGenderInfo } = this.props;
    const { customGender, gender, showCustomGenderTextfield } = this.state;
    const genderData = showCustomGenderTextfield
      ? { gender: UNSPECIFIED, custom_gender: customGender.slice(0, 30) }
      : { gender };

    updateGenderInfo(genderData).then(() => {
      this.setUserStateCompletedGenderStep();
      track(PCONST.thrift.EventType.CLICK, {
        component: PCONST.thrift.ComponentType.BROWSER,
        element: PCONST.thrift.ElementType.GENDER_BUTTON,
        view: isRenux
          ? PCONST.thrift.ViewType.REDO_ORIENTATION
          : PCONST.thrift.ViewType.ORIENTATION,
        viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_GENDER_STEP,
      });
      incrementNuxStep();
    });
  }

  submitHandler(e: SyntheticEvent<>) {
    e.preventDefault();
  }

  render() {
    const {
      isRenux,
      personalizationCopy: { headerText, subHeaderText },
    } = this.props;
    const {
      customGender,
      showCustomGenderDoneButton,
      showCustomGenderTextfield,
      showNextButton,
    } = this.state;

    const customGenderInput = showCustomGenderTextfield ? (
      <Fragment>
        <Box marginEnd={5}>
          <input
            className={'NuxGenderSelector'}
            data-test-id="nux-gender-unspecified-input"
            id="customGender"
            onChange={this.setCustomGender}
            // If page refreshes, show the customGender user has already entered
            placeholder={
              customGender ||
              i18n._('Enter custom gender', 'placeholder text for custom gender text field')
            }
            style={
              showCustomGenderDoneButton
                ? INLINE_GENDER_EDIT_STYLE_WITH_BUTTON
                : INLINE_GENDER_EDIT_STYLE
            }
            type="text"
          />
        </Box>
        {!showNextButton &&
          showCustomGenderDoneButton && (
            <div data-test-id="nux-gender-unspecified-done-btn">
              <Button
                color="red"
                inline
                onClick={() => this.updateGenderCompleteStep()}
                text={i18n._('Done', 'button text for entering a custom gender type')}
              />
            </div>
          )}
      </Fragment>
    ) : (
      <Heading size="sm">{i18n._('Non-binary', 'Non-binary label for custom gender')}</Heading>
    );

    return (
      <Box
        display="flex"
        direction="column"
        height="100%"
        justifyContent="around"
        paddingY={8}
        width="100%"
      >
        <Box
          direction="column"
          display="flex"
          paddingX={8}
          dangerouslySetInlineStyle={
            //overrides left-justified multiline headings
            { __style: { textAlign: 'center' } }
          }
        >
          <Heading size={isRenux ? 'xs' : 'sm'}>{headerText}</Heading>
          {isRenux && (
            <Text align="center" size="xl">
              {subHeaderText}
            </Text>
          )}
        </Box>
        <Box>
          <Divider />
          <FullRadioButton
            isChecked={!showCustomGenderTextfield && this.state.gender.toLowerCase() === FEMALE}
            label={FEMALE}
            selectGender={() => this.onSelectGender(FEMALE)}
            value={<Heading size="sm">{i18n._('Female')}</Heading>}
          />
          <FullRadioButton
            isChecked={!showCustomGenderTextfield && this.state.gender.toLowerCase() === MALE}
            label={MALE}
            selectGender={() => this.onSelectGender(MALE)}
            value={<Heading size="sm">{i18n._('Male')}</Heading>}
          />
          <FullRadioButton
            isChecked={showCustomGenderTextfield}
            label={UNSPECIFIED}
            selectGender={() => this.onSelectGender(UNSPECIFIED)}
            value={customGenderInput}
          />
        </Box>
        {showNextButton && (
          <Box data-test-id="nux_gender_next_btn" marginTop={8} marginStart={8} marginEnd={8}>
            <Button
              accessibilityLabel={i18n._('Next', 'go to next nux step')}
              color="red"
              // This button only shows up when on gender step for 2nd time, so the only
              // case it is disabled is for non-binary and no text input
              disabled={showCustomGenderTextfield && this.state.customGender.length === 0}
              onClick={() => this.updateGenderCompleteStep()}
              text={i18n._('Next', 'next nux step button')}
              type="button"
            />
          </Box>
        )}
      </Box>
    );
  }
}

const mapStateToProps = ({ viewer }: ReduxState): StateProps => ({
  gender: viewer.gender ? viewer.gender : '',
  customGender: viewer.customGender ? viewer.customGender : '',
});

const mapDispatchToProps = (dispatch: Dispatch<any>, props: OwnProps): DispatchProps => ({
  dispatchSetNuxStepProgress: (step: NuxStep) => dispatch(setNuxProgress(step)),
  setNuxGenderProgress: (progress) => dispatch(setUserState(NUX_GENDER_STEP_STATUS, progress)),
  updateGenderInfo: (genderData) => dispatch(updateUser({ updateUserData: genderData })),
});

export default compose(
  connect<ConnectProps, OwnProps, _, DispatchProps, _, _>(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withRequestContext,
)(NuxGenderStep);
